import { FC, ReactNode, useContext, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import {
  Avatar,
  Box,
  Container as MaterialUIContainer,
  IconButton,
  Link,
  Menu,
  MenuItem,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

import { UserContext } from '../..';
import NavTiles from '../NavTiles';
import {
  BrandOrnament,
  BrandTitle,
  ClientLogoPlaceholder,
  LogoLockup,
  OberthurLogo,
} from '../Brand';
import { clearSession } from '../../utils/session';
import { ROLE_ADMIN } from '../../constants';
import {
  colors,
  fontFamilies,
  fontWeights,
  radii,
  shadows,
} from '../../theme/tokens';

// Shell of the authenticated screens ("Common structure" in DESIGN.md).
//
// The XD has no sidebar: navigation is the four tiles in the header. This
// component keeps the `Container` name so that the pages that already import
// it do not change, but there is no longer a Drawer nor an open state, and
// the content takes the full width.
//
// Responsive (ROADMAP.md, Part 2): below `tablet` (768 px) the header
// compacts — logo on the left, avatar with a menu on the right, the date
// disappears — and the tiles switch to a 2×2 grid (see NavTiles).
//
// Services and functions administration (`/services`, `/functions`): the
// XD gives them neither a tile nor a link, and without a sidebar they would
// not be reachable. For the administrator they go as header links next to
// "Mon profil". Own decision; no tiles are added.
//
// LTU asked (feedback on the mobile Home) to drop Services/Fonctions from
// the mobile avatar menu — the desktop row keeps them, only the compact
// mobile menu narrows to "Mon profil" / "Déconnexion". They stay reachable
// on mobile through the desktop-width header (tablet and up).

interface Props {
  sx?: SxProps<Theme>;
  /**
   * The tiles go on the "hub" pages (listings, users list) and not on
   * Résultats. They are shown by default.
   */
  showTiles?: boolean;
  /**
   * Desktop only: no white card around the content and wider side margins,
   * so the content sits directly on the page background as in the XD Home.
   */
  plain?: boolean;
  children?: ReactNode;
}

// "Jeudi 2 avril 2026". `dayjs/locale/fr` writes the day and month in
// lowercase; DatePicker overrides them to uppercase for its calendar, so
// everything is normalised to lowercase before capitalising the initial, and
// the result is the same wherever it comes from.
const formatToday = (): string => {
  const raw = dayjs().locale('fr').format('dddd D MMMM YYYY').toLowerCase();
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

// XD header links ("Mon profil", "Services"…): small, regular-weight blue
// Poppins, not the bold orange of the shared `actionLink` variant.
const HEADER_LINK_SX = {
  color: colors.primary,
  fontWeight: fontWeights.regular,
} as const;

// Client feedback: the avatar menu is hidden on mobile for now; "Mon compte"
// and "Se déconnecter" are tiles in the navigation grid instead.
const MOBILE_AVATAR_MENU_ENABLED = false;

const HEADER_TITLE_SX = {
  fontSize: '2rem',
  lineHeight: 1.1,
} as const;

const Container: FC<Props> = ({
  sx,
  showTiles = true,
  plain = false,
  children,
}) => {
  const userContext = useContext(UserContext);
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // "Bonjour [Prénom]": the name comes from the session context. Login
  // receives it from `POST /api/users/login` (since `ac614a0`) and stores it
  // in localStorage (ARCHIVOS_LOGIN), from where `readSessionUser` rehydrates
  // it on reload as cosmetic data; the token neither carries it nor needs to.
  // The former useDisplayName hook, which requested it from `GET /users/:id`,
  // was redundant and was removed.
  const firstname = userContext?.mainUser?.firstname;
  const greeting = firstname ? `Bonjour ${firstname}` : 'Bonjour';
  const isAdmin = userContext?.mainUser?.role === ROLE_ADMIN;

  const headerLinks = [
    { to: '/profile', label: 'Mon profil' },
    ...(isAdmin
      ? [
          { to: '/services', label: 'Services' },
          { to: '/functions', label: 'Fonctions' },
        ]
      : []),
  ];
  // Mobile avatar menu: "Mon profil" only, per LTU's feedback.
  const mobileHeaderLinks = headerLinks.filter((item) => item.to === '/profile');

  const signOut = () => {
    setMenuAnchor(null);
    clearSession();
    userContext?.updateUser(null);
    navigate('/login');
  };

  return (
    <MaterialUIContainer
      maxWidth={false}
      disableGutters
      sx={{
        position: 'relative',
        minHeight: '100%',
        overflow: 'hidden',
        backgroundColor: colors.surface,
        ...sx,
      }}
    >
      {/* Ornament: the XD circle composition, in light grey over the page
          background and always below the content. */}
      <BrandOrnament position="fixed" opacity={0.8} color={colors.border} />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box
          component="header"
          sx={(theme) => ({
            backgroundColor: colors.white,
            boxShadow: shadows.card,
            px: 4,
            pt: 2.5,
            pb: showTiles ? 3 : 2.5,
            // LTU (mobile Home feedback): the tiles and the header get
            // tighter here so the list below fits with less scrolling.
            [theme.breakpoints.down('tablet')]: {
              px: 2,
              pt: 1.25,
              pb: showTiles ? 1.25 : 1.5,
            },
          })}
        >
          {/* Desktop and tablet */}
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 3,
              color: colors.primary,
              [theme.breakpoints.down('tablet')]: { display: 'none' },
            })}
          >
            {/* The brand block links to the Home: screens without tiles
                (Résultats) would otherwise have no way back. */}
            <Link
              component={RouterLink}
              to="/my-documents"
              aria-label="Accueil"
              underline="none"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                color: 'inherit',
              }}
            >
              <LogoLockup height={40} />
              <Box>
                {/* XD header: the date is blue over the brand lockup. */}
                <Typography
                  component="p"
                  sx={{
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    color: colors.primary,
                    textTransform: 'none',
                  }}
                >
                  {formatToday()}
                </Typography>
                <BrandTitle
                  component="h1"
                  titleSx={HEADER_TITLE_SX}
                  subtitleSx={{ color: colors.textBase }}
                />
              </Box>
            </Link>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 1,
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2.5 }}>
                {headerLinks.map((item) => (
                  <Link
                    key={item.to}
                    component={RouterLink}
                    to={item.to}
                    variant="actionLink"
                    underline="hover"
                    sx={HEADER_LINK_SX}
                  >
                    {item.label}
                  </Link>
                ))}
                <Link
                  component="button"
                  type="button"
                  variant="actionLink"
                  underline="hover"
                  onClick={signOut}
                  sx={HEADER_LINK_SX}
                >
                  Déconnexion
                </Link>
              </Box>
              {/* XD header: "Bonjour [Prénom]" in Poppins (sans), primary blue;
                  the serif is reserved for the brand title. */}
              <Typography
                component="p"
                sx={{
                  fontFamily: fontFamilies.sans,
                  fontWeight: fontWeights.semiBold,
                  fontSize: '1.25rem',
                  lineHeight: 1.3,
                  color: colors.primary,
                }}
              >
                {greeting}
              </Typography>
            </Box>
          </Box>

          {/* Mobile (smartphone XD, S2): one row with the colour isotype, the
              client slot and the "archiv.OS" lockup, then the date and the
              greeting centred below. */}
          <Box
            sx={(theme) => ({
              display: 'none',
              flexDirection: 'column',
              gap: 1,
              color: colors.primary,
              [theme.breakpoints.down('tablet')]: { display: 'flex' },
            })}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Link
                component={RouterLink}
                to="/my-documents"
                aria-label="Accueil"
                underline="none"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  color: 'inherit',
                  minWidth: 0,
                }}
              >
                <OberthurLogo height={40} />
                <ClientLogoPlaceholder height={40} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    component="h1"
                    variant="h1"
                    sx={{ fontSize: '1.25rem', lineHeight: 1, color: 'inherit' }}
                  >
                    archiv.OS
                  </Typography>
                  <Typography
                    component="p"
                    sx={{
                      fontSize: '0.5625rem',
                      lineHeight: 1.3,
                      color: colors.textBase,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    par Oberthur Solutions
                  </Typography>
                </Box>
              </Link>
              {/* Hidden for now to match the XD exactly: account and logout
                  live in the navigation tiles ("Mon compte", "Se déconnecter"). */}
              {MOBILE_AVATAR_MENU_ENABLED && (
                <IconButton
                  aria-label="Menu du compte"
                  aria-haspopup="menu"
                  aria-expanded={menuAnchor ? 'true' : undefined}
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  sx={{ p: 0.5 }}
                >
                  <Avatar
                    sx={{
                      bgcolor: colors.primary,
                      color: colors.white,
                      width: 36,
                      height: 36,
                      fontSize: '0.875rem',
                      fontWeight: fontWeights.semiBold,
                    }}
                  >
                    {firstname ? firstname.charAt(0).toUpperCase() : (
                      <PersonOutlineIcon fontSize="small" />
                    )}
                  </Avatar>
                </IconButton>
              )}
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Typography
                component="p"
                sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: colors.primary }}
              >
                {formatToday()}
              </Typography>
              <Typography
                component="p"
                sx={{
                  fontFamily: fontFamilies.sans,
                  fontWeight: fontWeights.semiBold,
                  fontSize: '1.25rem',
                  lineHeight: 1.3,
                  color: colors.primary,
                }}
              >
                {greeting}
              </Typography>
            </Box>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem disabled sx={{ opacity: '1 !important' }}>
                <Typography sx={{ fontWeight: fontWeights.medium }}>
                  {greeting}
                </Typography>
              </MenuItem>
              {mobileHeaderLinks.map((item) => (
                <MenuItem
                  key={item.to}
                  onClick={() => {
                    setMenuAnchor(null);
                    navigate(item.to);
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
              <MenuItem onClick={signOut}>Déconnexion</MenuItem>
            </Menu>
          </Box>

          {showTiles && (
            <Box
              sx={(theme) => ({
                mt: 3,
                [theme.breakpoints.down('tablet')]: { mt: 1.25 },
              })}
            >
              <NavTiles />
            </Box>
          )}
        </Box>

        {/* Content */}
        <Box
          component="main"
          sx={(theme) => ({
            p: 3,
            // The XD list does not span the viewport: it keeps a margin of
            // roughly a tenth of the width on each side.
            ...(plain && { px: 'clamp(24px, 8vw, 160px)' }),
            [theme.breakpoints.down('tablet')]: { p: 1.5, pt: 1 },
          })}
        >
          <Box
            sx={(theme) => ({
              backgroundColor: colors.white,
              borderRadius: `${radii.card}px`,
              boxShadow: shadows.card,
              p: 2,
              ...(plain && {
                backgroundColor: 'transparent',
                boxShadow: 'none',
                p: 0,
              }),
              [theme.breakpoints.down('tablet')]: {
                backgroundColor: colors.white,
                boxShadow: shadows.card,
                p: 1.5,
              },
            })}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </MaterialUIContainer>
  );
};

export default Container;
